import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import smartHoodie from "@/assets/finfox/smart-hoodie.png";
import happyHoodie from "@/assets/finfox/happy-hoodie.png";
import celebratingHoodie from "@/assets/finfox/celebrating-hoodie.png";
import curiousHoodie from "@/assets/finfox/curious-hoodie.png";
import {
  getFinFoxRunnerEnabled,
  setFinFoxRunnerEnabled,
  subscribeFinFoxRunner,
} from "@/lib/finfoxPrefs";
import { subscribeFinFox } from "@/lib/finfoxBus";


const FRAMES = [smartHoodie, happyHoodie, celebratingHoodie, curiousHoodie];

const QUIPS = [
  "Compounding is my cardio 💪",
  "Diversify or get dizzy 🌀",
  "Risk managed, vibes intact ✨",
  "Read the balance sheet, not the hype 📚",
  "Time in market > timing market ⏳",
  "Long on learning 🎓",
  "Margin of safety, always 🛡️",
  "Process > outcome. Always. 🎯",
];

const ROUTE_QUIPS: Record<string, string[]> = {
  "/financial-league": [
    "Thesis first, trade second 📝",
    "Position size > stock pick 🎯",
    "Cut losers, let winners run 🏃",
  ],
  "/financial-tinder": [
    "Two cards a day, jargon away 🃏",
    "Read the 'why', then swipe 👀",
  ],
  "/community": [
    "Share reasoning, not just returns 💬",
  ],
  "/trading-journal": [
    "Tag the emotion, not the trade 🧭",
  ],
  "/dashboard": [
    "Tiny daily reps. Big yearly results. 📈",
  ],
};

// Single calm pace — no more speed meter.
const RUN_DURATION = 8; // seconds per cross
const RUN_INTERVAL = 11000; // ms between runs

/**
 * FinFox Runner — friendly mascot that calmly strolls across the header
 * with the occasional finance quip.
 */
export default function FinFoxRunner() {
  const [enabled, setEnabled] = useState(getFinFoxRunnerEnabled);
  const [paused, setPaused] = useState(false);
  const [runId, setRunId] = useState(1);
  const [dir, setDir] = useState<"ltr" | "rtl">("ltr");
  const [frame, setFrame] = useState(0);
  const [quip, setQuip] = useState<string | null>("Hi! I'm FinFox 🦊");
  const location = useLocation();

  useEffect(() => subscribeFinFoxRunner(setEnabled), []);

  // When FinFox has a playful jab for the student, the running fox says it.
  useEffect(
    () =>
      subscribeFinFox((msgs) => {
        const jab = [...msgs].reverse().find((m) => m.kind === "troll" && m.jab)?.jab;
        if (!jab) return;
        setQuip(jab);
        setRunId((rid) => rid + 1);
      }),
    []
  );


  useEffect(() => {
    if (!enabled || paused) return;
    const pickQuip = (): string => {
      const routeKey = Object.keys(ROUTE_QUIPS).find((k) => location.pathname.startsWith(k));
      const pool = routeKey && Math.random() < 0.55 ? ROUTE_QUIPS[routeKey] : QUIPS;
      return pool[Math.floor(Math.random() * pool.length)];
    };
    const startRun = () => {
      setDir((d) => (d === "ltr" ? "rtl" : "ltr"));
      setFrame((f) => (f + 1) % FRAMES.length);
      setRunId((rid) => rid + 1);
      // Show a quip on ~60% of runs
      if (Math.random() < 0.6) {
        setQuip(pickQuip());
        setTimeout(() => setQuip(null), 3200);
      } else {
        setQuip(null);
      }
    };
    const q = setTimeout(() => setQuip(null), 2600);
    const t = setInterval(startRun, RUN_INTERVAL);
    return () => {
      clearTimeout(q);
      clearInterval(t);
    };
  }, [enabled, paused, location.pathname]);

  if (!enabled) return null;

  const baseAnim = dir === "ltr" ? "finfox-run-ltr" : "finfox-run-rtl";

  return (
    <>
      <style>{`
        @keyframes finfox-run-ltr {
          0%   { transform: translateX(-80px) rotate(-2deg); }
          50%  { transform: translateX(50vw) translateY(-2px) rotate(1deg); }
          100% { transform: translateX(calc(100vw + 120px)) rotate(2deg); }
        }
        @keyframes finfox-run-rtl {
          0%   { transform: translateX(calc(100vw + 80px)) scaleX(-1) rotate(2deg); }
          50%  { transform: translateX(50vw) translateY(-2px) scaleX(-1) rotate(-1deg); }
          100% { transform: translateX(-160px) scaleX(-1) rotate(-2deg); }
        }
        @keyframes finfox-bob {
          0%, 100% { transform: translateY(0); }
          50%      { transform: translateY(-2px); }
        }
        @keyframes finfox-quip-pop {
          0%   { opacity: 0; transform: translateY(4px) scale(0.9); }
          100% { opacity: 1; transform: translateY(0) scale(1); }
        }
      `}</style>

      <div
        className="fixed top-0 left-0 right-0 z-[60] overflow-hidden"
        style={{ height: 80, pointerEvents: "none" }}
      >
        <div
          key={runId}
          className="absolute top-2"
          style={{
            animation: `${baseAnim} ${RUN_DURATION}s cubic-bezier(0.42, 0, 0.58, 1) forwards`,
            willChange: "transform",
          }}
        >
          <div className="relative" style={{ animation: "finfox-bob 0.9s ease-in-out infinite" }}>
            <img
              src={FRAMES[frame]}
              alt="FinFox — click to pause"
              title={paused ? "Click to wake FinFox" : "Click to pause FinFox"}
              onClick={(e) => {
                e.stopPropagation();
                setPaused((p) => !p);
                setQuip(paused ? "Back at it 🦊" : "Taking a breather ☕");
                setTimeout(() => setQuip(null), 1800);
              }}
              className="relative h-12 w-12 rounded-full bg-gradient-to-br from-orange-200 to-amber-300 shadow-lg ring-2 ring-background object-cover cursor-pointer hover:scale-110 transition-transform"
              style={{ pointerEvents: "auto" }}
              draggable={false}
            />

            {quip && (
              <div
                className="absolute -top-3 left-14 whitespace-nowrap rounded-2xl bg-card border border-border shadow-md px-3 py-1.5 text-[11px] font-semibold text-foreground"
                style={{
                  transform: dir === "rtl" ? "scaleX(-1)" : undefined,
                  animation: "finfox-quip-pop 0.35s ease-out both",
                }}
              >
                <span className="mr-1">🦊</span>{quip}
                <span className="absolute -bottom-1 left-3 h-2 w-2 rotate-45 bg-card border-b border-r border-border" />
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

/** Helper to toggle runner from anywhere */
export function setFinFoxRunner(on: boolean) {
  setFinFoxRunnerEnabled(on);
}
